/**
 * THE DRAFT THAT SURVIVES THE TAB — every access wrapped, nothing load bearing.
 *
 * ── THE DEFECT THIS ANSWERS ───────────────────────────────────────────────────────────
 * The editor refuses to save an incomplete graph, and `grep localStorage` across the whole
 * section returned zero. So a sixteen-step booking task had to be built to full validity in
 * one sitting or lost — and the one thing an owner most often leaves the page for, mid-task,
 * is to go and connect the system the task is about.
 *
 * The server is right to refuse a partial graph (`schema.ts:144-155` says so in as many
 * words), so the fix is not a permissive server. It is that the work stops living only in
 * a component that a route change unmounts.
 *
 * ── WHERE THIS IS THE SAFETY NET, AND WHERE IT IS NOT ─────────────────────────────────
 * On the CREATE path nothing exists on the server until the last click, so this is the only
 * copy of the work and it is the whole mechanism.
 *
 * On the EDIT path it is a net under a real save: every Done writes here, and a `PUT` fires
 * the moment the draft is one the server would accept. A draft that is still invalid has
 * nowhere else to be. That is the defect above, and this is where it is answered.
 *
 * ── WHY EVERY SINGLE ACCESS IS WRAPPED ────────────────────────────────────────────────
 * `localStorage` is not a variable, it is a host object with four separate ways to fail, and
 * three of them are things a real owner does:
 *
 *   1. it can be ABSENT — server-side rendering, a stripped test environment;
 *   2. reading the PROPERTY ITSELF can throw, which is what a browser with site data blocked
 *      does, before any method is ever called;
 *   3. `setItem` can throw on quota, and does so in a private window with a zero quota;
 *   4. it can come back EMPTY at any moment — cleared site data, a different device, a
 *      private window from an hour ago.
 *
 * So: a `try`/`catch` around each of the four operations, no module-level capture of the
 * store (a page that opened before storage was granted would hold `null` for ever), and
 * every function total. THE PAGE MUST RENDER CORRECTLY WITH ALL OF THIS RETURNING NOTHING
 * — which is why `write` reports whether it stuck rather than throwing, and why `read` has
 * exactly one failure value for every one of the four.
 *
 * ── AND WHAT IS DELIBERATELY NOT KEPT HERE ────────────────────────────────────────────
 * No credential ever reaches this file. A connection's sign-in is write-only and lives on
 * the connection row; a task names a system, it never carries the way into it.
 */

/** One task's drawer. `new` is the create path, which has no id to key on yet. */
export const NEW_TASK = 'new';

/**
 * The key one draft lives under.
 *
 * The account id is in it because one browser signs into several accounts and a draft is
 * not portable between them: a task naming account 1's systems, restored into account 2, is
 * a graph that refers to things that do not exist there.
 */
export const draftKey = (accountId, taskId) =>
  `waslo:task-draft:${String(accountId ?? '')}:${String(taskId ?? NEW_TASK)}`;

/**
 * The store, or `null`. Read fresh every time and never cached.
 *
 * The property access is INSIDE the `try` on purpose: a browser with site data blocked
 * throws on `globalThis.localStorage` itself, before `getItem` is reached.
 */
const store = () => {
  try {
    const found = globalThis.localStorage;
    return found && typeof found.getItem === 'function' ? found : null;
  } catch {
    return null;
  }
};

const isObject = value =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * What was kept, or `null`.
 *
 * ONE FAILURE VALUE FOR ALL FIVE FAILURES — absent store, a throwing store, nothing under
 * the key, text that is not JSON, and text that parses into something that is not a kept
 * draft. A caller that has to tell them apart would be a caller writing a second recovery
 * flow for a case it cannot do anything about; there is one thing to offer either way, and
 * it is the draft or it is not.
 *
 * @returns {{draft: object, savedAt: number}|null}
 */
export const read = (accountId, taskId) => {
  const held = store();
  if (!held) return null;
  let raw;
  try {
    raw = held.getItem(draftKey(accountId, taskId));
  } catch {
    return null;
  }
  if (typeof raw !== 'string' || raw === '') return null;
  try {
    const parsed = JSON.parse(raw);
    if (!isObject(parsed) || !isObject(parsed.draft)) return null;
    // A stamp that is not a finite number is a stamp that cannot be aged, and an age is
    // half of what the offer to restore says out loud ("from 12 minutes ago").
    if (!Number.isFinite(parsed.savedAt)) return null;
    return { draft: parsed.draft, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
};

/**
 * Keep this draft, and say whether it stuck.
 *
 * A BOOLEAN RATHER THAN A THROW, and it is the difference between a net and a trap: quota
 * is a normal outcome in a private window, and a composer that let it escape would lose the
 * Done the owner just pressed to a mechanism whose entire job is not losing it. The caller
 * that wants to tell her "your work is only in this tab" has its answer; the caller that
 * does not may ignore it and is still correct.
 *
 * `now` is a parameter so the whole file stays testable without a fake clock.
 */
export const write = (accountId, taskId, draft, now = Date.now()) => {
  const held = store();
  if (!held) return false;
  try {
    held.setItem(
      draftKey(accountId, taskId),
      JSON.stringify({ savedAt: now, draft })
    );
    return true;
  } catch {
    return false;
  }
};

/**
 * Forget this draft. Silent, and deliberately so: it runs after a save that has already
 * succeeded and after a Discard the owner asked for, and in both the work is safe whether
 * or not the key was ever there.
 */
export const clear = (accountId, taskId) => {
  const held = store();
  if (!held) return;
  try {
    held.removeItem(draftKey(accountId, taskId));
  } catch {
    // Nothing to do and nothing to say: the caller's work is already elsewhere.
  }
};

/**
 * How old a kept draft is, in milliseconds, or `null` when there is nothing to age.
 *
 * NEVER NEGATIVE. A clock that moved back — a machine that resynced, a draft written in
 * another timezone offset — would otherwise produce "saved in 3 hours", and an offer to
 * restore work from the future reads as a bug in the product rather than in the clock.
 *
 * @param {{savedAt: number}|null} record What `read` returned.
 */
export const age = (record, now = Date.now()) => {
  if (!isObject(record) || !Number.isFinite(record.savedAt)) return null;
  if (!Number.isFinite(now)) return null;
  return Math.max(0, now - record.savedAt);
};

export default read;
